import { Injectable } from "@angular/core";

import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { CalculationSettingsModel, ConfigurationProcedureModel, RNAVModel } from 'src/app/models/configuration';
import { AntennaTypes, ConfigurationConstants, FeatureType, NavaidTypes } from 'src/app/models/EnumTypes';
import { PerformanceAnalysisDTO } from 'src/app/models/performanceAnalysisDTO';
import { ProcedureVerticalProfileComponent } from 'src/app/popup/calculations/procedure-vertical-profile/procedure-vertical-profile.component';
import { CalculationsService } from 'src/app/services/calculations.service';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { ModalControlService } from 'src/app/services/modal-control.service';
import { ModalConfirmationService } from 'src/app/services/modal-confirmation.service';
import { PointsService } from 'src/app/services/points.service';
import { TabsService } from 'src/app/services/tabsService';
import { ValidationModalService } from 'src/app/services/validation-modal.service';
import { NavaidModel } from "../models/navaid";
import { PopupPerformanceAnalysisComponent } from "../popup/calculations/performance-analysis/performance-analysis.component";
import { ProcedureControlService } from "./procedure-control.service";
import { CalculationsControlService } from "./calculations-control.service";
import { PendingCalculationType } from "../models/pendingCalculation";
import { Geometry } from "ol/geom";
import { Feature } from "ol";
import { ProcedureModel } from "../models/procedure";
import GeoJSON from 'ol/format/GeoJSON';
import { UtilsService } from "./utils.service";
@Injectable({
  providedIn: 'root'
})
export class CalculationValidation{

  constructor(
    private modalService : ModalControlService,
    private modalConfirmation: ModalConfirmationService,
    private popupService : BsModalService,
    private configurationService : ConfigurationService,
    private pointsService: PointsService,
    private validationModalService: ValidationModalService,
    private calculationsControlService: CalculationsControlService,
    private calculationService: CalculationsService,
    private utilsService: UtilsService
  ){}

  validPerformanceFeatures( minNavaids: number, numProcedures: number): boolean {
    let numberOfValidDME_ILS = this.pointsService.getSelectedNavaidsOfType(NavaidTypes.DME_ILS)
    .filter(feature =>{
      const navaid = this.pointsService.transformFeatureInNavaid(feature);
      return this.calculationsControlService.validDMEILSNavaid(navaid);
    }).length;
    return (this.pointsService.getRNAVNavaids().length + numberOfValidDME_ILS  >= minNavaids
     && this.pointsService.getSelectedFeaturesOfType(FeatureType.Procedure).length  == numProcedures)

  }




  async openPerformanceAnalysisPopupInitialization(){
    let minNavaids = 2;
    let numProcedures = 1;
    if(!this.validPerformanceFeatures(minNavaids,numProcedures)){
      this.modalService.showMesssageModal("Warning",
       `Please select at least ${minNavaids} NavAid of type RNAV and only ${numProcedures} Procedure`);
      return;
    }

    let procedureFeature = this.pointsService.getSelectedFeaturesOfType(FeatureType.Procedure);
    const data = this.pointsService.transformFeatureInProcedureCalculationDTO(procedureFeature[0]);
    const performanceAnalysisDTO   = new PerformanceAnalysisDTO();

    performanceAnalysisDTO.procedure = data;

    performanceAnalysisDTO.procedureModel = this.pointsService.transformFeatureInProcedure(procedureFeature[0]);
    performanceAnalysisDTO.procedureSettings = ConfigurationProcedureModel.mapToCalculation(this.configurationService.currentConfiguration.configurationProcedure);
    const geoJsonFormat = new GeoJSON();
    performanceAnalysisDTO.procedureFeatureJson = geoJsonFormat.writeFeature(procedureFeature[0]);

    const navaidsFeaturesSelected = this.pointsService.getSelectedFeaturesOfType(FeatureType.Navaid);

    // Check for any waypoint with aircraftheight below terrain height using helper method
    const hasBelowTerrain = await this.hasWaypointBelowTerrain(data?.waypoints);
    if (hasBelowTerrain) {
      this.modalConfirmation.showMesssageConfirmationModal(
        'Warning',
        'The height of the coordinate point in the procedure is either undefined or below ground level. Would you like to continue?',
        'Yes',
        'No',
        () => {
          this.calculationsControlService.getNavaidDTOCalculationArrayInitialization(performanceAnalysisDTO, navaidsFeaturesSelected, PendingCalculationType.PERFORMANCE_ANALYSIS);
        },
        () => {
          // Do nothing if cancelled
        }
      );
    } else {
      this.calculationsControlService.getNavaidDTOCalculationArrayInitialization(performanceAnalysisDTO, navaidsFeaturesSelected, PendingCalculationType.PERFORMANCE_ANALYSIS);
    }
  }

  /**
   * Checks if any waypoint has aircraftheight below terrain height or undefined.
   * Returns true if any such waypoint is found.
   */
  private async hasWaypointBelowTerrain(waypoints: any[]): Promise<boolean> {
    if (!waypoints?.length) return false;
    for (const wp of waypoints) {
      if (wp?.latitude != null || wp?.longitude != null || wp?.aircraftheight != null) {
        try {
          const terrainObs = await this.pointsService.getTerrainElevation(wp.latitude, wp.longitude);
          let terrain: number;
          if (terrainObs?.subscribe) {
            terrain = await new Promise<number>((resolve, reject) => {
              terrainObs.subscribe({
                next: val => resolve(val),
                error: err => reject(err)
              });
            });
          } else if (typeof terrainObs === 'number') {
            terrain = terrainObs;
          } else {
            return true;
          }
          if (wp.aircraftheight < terrain) {
            return true;
          }
        } catch (e) {
          console.error('Error retrieving terrain elevation: ' + wp.id, e);
          continue;
        }
      } else {
        return false;
      }
    }
    return false;
  }

  resumeOpenPerformanceAnalysisPopup(selectedFeatures: Feature<Geometry>[], performanceAnalysisDTO: PerformanceAnalysisDTO, navaidsTerrainHeight: number[]){
    this.pointsService.selectedProcedure = performanceAnalysisDTO.procedureModel;
    try {
      const ignoredNavaids: NavaidModel[] = [];
      const invalidNavaids: NavaidModel[] = [];
      // Iterating backwards to avoid index shifting issues when splicing
      for (let i = selectedFeatures.length - 1; i >= 0; i--) {
        const navFeature = selectedFeatures[i];
        const navaid: NavaidModel = this.pointsService.transformFeatureInNavaid(navFeature);
        navaid.effectiveHeight = performanceAnalysisDTO.navaids[i]?.effectiveHeight;
        if (!this.calculationsControlService.validNavaidTypeForPerformanceCalculation(navaid)) {
          ignoredNavaids.push(navaid);
          performanceAnalysisDTO.navaids.splice(i, 1);
          if (navaidsTerrainHeight) {
            navaidsTerrainHeight.splice(i, 1);
          }
          continue;
        }

        let terrainElevation: number;
        if (navaidsTerrainHeight) {
          terrainElevation = navaidsTerrainHeight[i];
        }
        if (!this.utilsService.isValidNavaid(navaid, terrainElevation)) {
          invalidNavaids.push(navaid);
        }
      }
      if(ignoredNavaids.length > 0 || invalidNavaids.length > 0){
        this.validationModalService.showPerformanceModal(performanceAnalysisDTO, invalidNavaids, ignoredNavaids, "PERFORMANCE ANALYSIS", navaidsTerrainHeight);
        return ;
      }
      const bsModalRef : BsModalRef = this.popupService.show(PopupPerformanceAnalysisComponent);
      bsModalRef.content.performanceAnalysisDTO = performanceAnalysisDTO;
    } catch (e){
      this.pointsService.selectedProcedure = null;
      throw e;
    }

  }

  openVerticalProfilePopupInitialization(){
    let minNavaids = 1;
    let numProcedures = 1;
    if(!this.validPerformanceFeatures(minNavaids,  numProcedures)){
      this.modalService.showMesssageModal("Warning",
       `Please select at least ${minNavaids} NavAid of type RNAV and only ${numProcedures} Procedure`);
      return;
    }

    let performanceAnalysisDTO : PerformanceAnalysisDTO = new PerformanceAnalysisDTO();
    for (let procedureFeature of this.pointsService.getSelectedFeaturesOfType(FeatureType.Procedure)) {
      const data = this.pointsService.transformFeatureInProcedureCalculationDTO(procedureFeature);
      performanceAnalysisDTO.procedure = data;
      performanceAnalysisDTO.procedureModel = this.pointsService.transformFeatureInProcedure(procedureFeature);

    }
    performanceAnalysisDTO.performanceType = 0;
    performanceAnalysisDTO.referenceairportLatitude = 0.0;
    performanceAnalysisDTO.referenceairportLongitude = 0.0;

    performanceAnalysisDTO.calculationSettings = CalculationSettingsModel.mapToCalculation(this.configurationService.currentConfiguration);
    performanceAnalysisDTO.rnav = RNAVModel.mapToCalculation(this.configurationService.currentConfiguration.rnav);

    const navaidsFeaturesSelected = this.pointsService.getSelectedFeaturesOfType(FeatureType.Navaid);

    this.calculationsControlService.getNavaidDTOCalculationArrayInitialization(performanceAnalysisDTO, navaidsFeaturesSelected, PendingCalculationType.VERTICAL_PROFILE);
  }

  resumeOpenVerticalProfilePopup(selectedFeatures: Feature<Geometry>[], performanceAnalysisDTO: PerformanceAnalysisDTO, navaidsTerrainHeight: number[]){
    this.pointsService.selectedProcedure = performanceAnalysisDTO.procedureModel;
    try {
      const ignoredNavaids: NavaidModel[] = [];
      const invalidNavaids: NavaidModel[] = [];

      for (let i = 0; i < selectedFeatures.length; i++) {
        const navFeature = selectedFeatures[i];
        const navaid: NavaidModel = this.pointsService.transformFeatureInNavaid(navFeature);
        navaid.effectiveHeight = performanceAnalysisDTO.navaids[i]?.effectiveHeight;
        if (!this.calculationsControlService.validNavaidTypeForPerformanceCalculation(navaid)) {
          ignoredNavaids.push(navaid);
          performanceAnalysisDTO.navaids.splice(i, 1);
          if (navaidsTerrainHeight) {
            navaidsTerrainHeight.splice(i, 1);
          }
          continue;
        }

        let terrainElevation: number;
        if (navaidsTerrainHeight) {
          terrainElevation = navaidsTerrainHeight[i];
        }
        if (!this.utilsService.isValidNavaid(navaid, terrainElevation)) {
          invalidNavaids.push(navaid);
        }

      }
      if(ignoredNavaids.length > 0 || invalidNavaids.length > 0){
        this.validationModalService.showPerformanceModal(performanceAnalysisDTO, invalidNavaids, ignoredNavaids, "VERTICAL PROFILE", navaidsTerrainHeight);
        return ;
      }
      const bsModalRef : BsModalRef = this.popupService.show(ProcedureVerticalProfileComponent);
      bsModalRef.content.performanceAnalysisDTO = performanceAnalysisDTO;
    } catch (e){
      this.pointsService.selectedProcedure = null;
      throw e;
    }
  }

  openRoutePerformanceReportPopupInitialization(){
    let minNavaids = 2;
    let numProcedures = 1;
    if(!this.validPerformanceFeatures(minNavaids,numProcedures)){
      this.modalService.showMesssageModal("Warning",
       `Please select at least ${minNavaids} NavAid of type RNAV and only ${numProcedures} Procedure`);
       return;
    }
    let performanceAnalysisDTO : PerformanceAnalysisDTO = new PerformanceAnalysisDTO();
    for (let procedureFeature of this.pointsService.getSelectedFeaturesOfType(FeatureType.Procedure)) {
      const data = this.pointsService.transformFeatureInProcedureCalculationDTO(procedureFeature);
      performanceAnalysisDTO.procedure = data;
      performanceAnalysisDTO.procedureModel = this.pointsService.transformFeatureInProcedure(procedureFeature);
      const geoJsonFormat = new GeoJSON();
      performanceAnalysisDTO.procedureFeatureJson = geoJsonFormat.writeFeature(procedureFeature);
    }

    performanceAnalysisDTO.performanceType = 0;
    performanceAnalysisDTO.referenceairportLatitude = 0.0;
    performanceAnalysisDTO.referenceairportLongitude = 0.0;

    performanceAnalysisDTO.calculationSettings = CalculationSettingsModel.mapToCalculation(this.configurationService.currentConfiguration);
    performanceAnalysisDTO.calculationSettings.geometricConstraints = true;
    performanceAnalysisDTO.rnav = RNAVModel.mapToCalculation(this.configurationService.currentConfiguration.rnav);
    performanceAnalysisDTO.procedureSettings = ConfigurationProcedureModel.mapToCalculation(this.configurationService.currentConfiguration.configurationProcedure);
    performanceAnalysisDTO.projectId = this.configurationService.currentConfiguration.projectId;

    const navaidsFeaturesSelected = this.pointsService.getSelectedFeaturesOfType(FeatureType.Navaid);

    this.calculationsControlService.getNavaidDTOCalculationArrayInitialization(performanceAnalysisDTO, navaidsFeaturesSelected, PendingCalculationType.PERFORMANCE_REPORT);
  }

  resumeOpenRoutePerformanceReportPopup(selectedFeatures: Feature<Geometry>[], performanceAnalysisDTO: PerformanceAnalysisDTO, navaidsTerrainHeight: number[]) {
    const ignoredNavaids: NavaidModel[] = [];
    const invalidNavaids: NavaidModel[] = [];

    for (let i = 0; i < selectedFeatures.length; i++) {
      const navFeature = selectedFeatures[i];
      const navaid: NavaidModel = this.pointsService.transformFeatureInNavaid(navFeature);
      navaid.effectiveHeight = performanceAnalysisDTO.navaids[i]?.effectiveHeight;
      if (!this.calculationsControlService.validNavaidTypeForPerformanceCalculation(navaid)) {
        ignoredNavaids.push(navaid);
        performanceAnalysisDTO.navaids.splice(i, 1);
        if (navaidsTerrainHeight) {
          navaidsTerrainHeight.splice(i, 1);
        }
        continue;
      }

      let terrainElevation: number;
      if (navaidsTerrainHeight) {
        terrainElevation = navaidsTerrainHeight[i];
      }
      if (!this.utilsService.isValidNavaid(navaid, terrainElevation)) {
        invalidNavaids.push(navaid);
      }

    }
    if(ignoredNavaids.length > 0 || invalidNavaids.length > 0){
      this.validationModalService.showPerformanceModal(performanceAnalysisDTO, invalidNavaids, ignoredNavaids, "PERFORMANCE REPORT", navaidsTerrainHeight);
      return;
    }

    this.calculationService.calculateRoutePerformanceReport(performanceAnalysisDTO);
  }

}
